/**
 * Restaurants API
 * 
 * API functions for fetching and updating restaurant data.
 */

import apiClient from './apiClient';

/**
 * List all restaurants
 * 
 * @returns {Promise<Array>} Array of restaurant objects with restaurantName and agentId
 */
export async function listRestaurants() {
  try {
    console.log('🍔 Fetching restaurants from API...');
    const response = await apiClient.get('/api/restaurants');
    console.log('🍔 Raw API response:', response);
    console.log('🍔 Response type:', typeof response);
    console.log('🍔 Is array?', Array.isArray(response));
    
    // Handle different response formats
    let restaurants = response;
    if (response && typeof response === 'object' && !Array.isArray(response)) {
      // If response is an object, check for data property
      restaurants = response.data || response.restaurants || [];
    }
    
    if (!Array.isArray(restaurants)) {
      console.warn('⚠️ Restaurants response is not an array:', restaurants);
      return [];
    }
    
    console.log(`🍔 Found ${restaurants.length} restaurants:`, restaurants);
    return restaurants;
  } catch (error) {
    console.error('❌ Error fetching restaurants:', error);
    throw error;
  }
}

/**
 * Update restaurant information. The assigned yapnNumber is read-only.
 * 
 * @param {string} locationId - Location ID of the restaurant
 * @param {object} data - Restaurant data to update
 * @param {string} [data.restaurantName] - Restaurant name
 * @param {string} [data.transferNumber] - General transfer destination (empty string clears it)
 * @param {string[]} [data.leadsEmail] - Lead email recipients for enabled locations (empty list clears recipients)
 * @param {string} [data.address] - Address
 * @param {string} [data.timeZone] - Time zone
 * @param {string} [data.primaryLanguage] - Primary language
 * @param {object} [data.hours] - Hours of operation
 * @param {object} [data.integrations] - Integration connections
 * @param {string} [data.menuFileUrl] - Menu file URL
 * @param {string} [data.greetingMessage] - Greeting message
 * @param {Array} [data.links] - Links array
 * @returns {Promise<object>} Updated restaurant object
 */
export async function updateRestaurant(locationId, data) {
  try {
    console.log(`🍔 Updating restaurant ${locationId}:`, data);
    const response = await apiClient.put(`/api/restaurants/${locationId}`, data);
    console.log('🍔 Restaurant updated:', response);
    return response;
  } catch (error) {
    console.error('❌ Error updating restaurant:', error);
    console.error('❌ Error details:', {
      message: error.message,
      code: error.code,
      status: error.status,
      response: error.response,
      data: error.response?.data
    });
    
    // Extract a more helpful error message
    let errorMessage = 'Failed to save. Please try again.';
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    } else if (error.code === 'NETWORK_ERROR') {
      errorMessage = 'Unable to connect. Please check your internet connection.';
    }
    
    const enhancedError = new Error(errorMessage);
    enhancedError.originalError = error;
    enhancedError.status = error.status || error.response?.status;
    throw enhancedError;
  }
}
